/**
 * Licensed view definitions — the single source of truth shared by the
 * Sidemenu filter and the profile-dropdown switch redirect (both must see
 * the same map, so it lives here rather than at either call site).
 *
 * Each view maps to the admin nav route segments it hides and any nav label
 * overrides. Keyed by route segment — the component right after /admin/ in
 * every item's _meta.itemActiveRoutes — so the master nav arrays need no
 * license awareness at all; hiding and relabeling happen at filter time.
 *
 * Mode values double as the persisted LicenseMode preference value, so this
 * module must stay dependency-free (imported by AuthHelpers and
 * FeatureFlagContext, which the hook itself depends on).
 */

export const LICENSE_MODES = {
  COMBINED: 'ai-human',
  AGENT_ONLY: 'agent-only',
  HUMAN_ONLY: 'human-only'
}

export const LICENSE_VIEWS = {
  [LICENSE_MODES.AGENT_ONLY]: {
    label: 'Agent Privilege View',
    landingRoute: '/admin/overview',
    hiddenRoutes: [
      'dashboard',
      'service-accounts',
      'clouds',
      'targets',
      'resources',
      'iam-roles',
      'saas-apps',
      'bundles',
      'addomaincontrollers'
    ],
    labels: { 'proxy-controllers': 'Gateway Management' }
  },
  [LICENSE_MODES.HUMAN_ONLY]: {
    label: 'PAM Privilege View',
    // the PAM Dashboard is this view's home — the Overview page is agent
    // posture and stays out of the nav (the route itself stays registered)
    landingRoute: '/admin/dashboard',
    hiddenRoutes: [
      'overview',
      'non-human-identities',
      'agentic-apps',
      'agent-config',
      'workload-agents',
      'personal-agents',
      'nhi-agents',
      'agent-approvals',
      'ai-policys'
    ],
    labels: {}
  },
  [LICENSE_MODES.COMBINED]: {
    label: 'Combined View',
    landingRoute: '/admin/overview',
    hiddenRoutes: [],
    labels: {}
  }
}

/**
 * Ordered [mode, label] entries for pickers — the profile dropdown lists all
 * three views, highlighting the active one.
 */
export const LICENSE_VIEWS_ENTRIES = Object.entries(LICENSE_VIEWS).map(([mode, v]) => [
  mode,
  v.label
])

/**
 * The management-surface label for the Proxy Controllers feature —
 * "Gateway Management" in the Agent Privilege view, "Proxy Management" in
 * every other. Entity naming ("Proxy Controller") is unaffected. Every
 * surface that prints the management name (nav, page heading, breadcrumb,
 * wizard and delete flows) reads this instead of a literal so the label
 * cannot fork between files.
 */
export const proxyManagementLabel = (licenseMode) =>
  LICENSE_VIEWS[licenseMode]?.labels?.['proxy-controllers'] || 'Proxy Management'

/**
 * The admin nav segment a route prefix points at — the component right
 * after /admin/ ('/admin/service-accounts/aws' → 'service-accounts'). Shared
 * for the same no-divergence reason as LICENSE_VIEWS itself.
 */
export const licenseRouteSegment = (itemActiveRoutes) => {
  const path = String(itemActiveRoutes || '')
  const adminIndex = path.indexOf('/admin/')
  if (adminIndex === -1) return ''
  return path.slice(adminIndex + '/admin/'.length).split('/')[0]
}
